import { updateVendorPoNumberPrefix } from '@api/vendors';
import useToaster from '@hooks/toaster/useToaster';
import { Select } from '@mantine/core';
import { type FC, useEffect, useMemo, useState } from 'react';

import { usePoPrefixOptions } from './usePoPrefixOptions';

/**
 * Which of the brand's PO number prefixes a new order to this vendor starts from, edited in
 * place. Saved on pick; clearing it leaves the order form's own picker in charge. A prefix the
 * vendor carries that Settings no longer lists is still shown, so it can be seen and cleared.
 */
export const VendorPoPrefixField: FC<{
    vendorId: string;
    poNumberPrefix: string | null | undefined;
    onSaved: (poNumberPrefix: string | null) => void;
}> = ({ vendorId, poNumberPrefix, onSaved }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const options = usePoPrefixOptions();
    const [value, setValue] = useState<string | null>(poNumberPrefix ?? null);
    const [saving, setSaving] = useState(false);
    useEffect(() => setValue(poNumberPrefix ?? null), [poNumberPrefix, vendorId]);

    const data = useMemo(
        () => (value && !options.includes(value) ? [...options, value] : options),
        [options, value],
    );

    const commit = async (next: string | null) => {
        setValue(next);
        if ((next ?? null) === (poNumberPrefix ?? null)) return;
        setSaving(true);
        try {
            await updateVendorPoNumberPrefix(vendorId, next);
            showToastSuccess({ title: next ? `PO prefix set to ${next}` : 'PO prefix cleared' });
            onSaved(next);
        } catch (e: any) {
            showToastError({ title: 'Could not save PO prefix', subtitle: e?.error?.message || e?.message || 'Please retry.' });
            setValue(poNumberPrefix ?? null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Select
            size="xs"
            data={data}
            value={value}
            onChange={(v) => void commit(v)}
            placeholder={options.length ? 'None — pick on the order' : 'No prefixes in Settings'}
            disabled={saving || (options.length === 0 && !value)}
            clearable
            searchable
        />
    );
};
